import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, ArrowRight, MapPin, Factory, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const IndustrialEPCDevon = () => {
  const towns = ['Exeter', 'Plymouth', 'Barnstaple', 'Newton Abbot', 'Tiverton', 'Exmouth', 'Honiton', 'Crediton', 'Ivybridge', 'Tavistock'];
  const industrialEstates = [
    { name: 'Marsh Barton', location: 'Exeter', desc: "One of the South West's largest trading estates with warehouses, workshops and light industrial units" },
    { name: 'Sowton Industrial Estate', location: 'Exeter', desc: 'Major distribution hub near junction 29 of the M5 motorway' },
    { name: 'Langage Business Park', location: 'Plymouth', desc: 'Modern industrial and logistics park serving South Devon' },
    { name: 'Roundswell Business Park', location: 'Barnstaple', desc: 'North Devon\'s principal industrial location' },
    { name: 'Brunel Industrial Estate', location: 'Newton Abbot', desc: 'Established trading estate with mixed industrial units' }
  ];

  const localSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Industrial EPC Assessments Devon",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Energy Performance Direct",
      "areaServed": { "@type": "State", "name": "Devon" }
    },
    "areaServed": { "@type": "State", "name": "Devon" },
    "serviceType": "Industrial Energy Performance Certificate"
  };

  return (
    <>
      <Helmet>
        <title>Industrial EPC Devon | Warehouse & Factory EPCs Exeter, Plymouth</title>
        <meta name="description" content="Industrial EPC assessments across Devon including Exeter, Plymouth, Barnstaple and Newton Abbot. Professional warehouse and factory EPC surveys for industrial landlords and developers." />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/industrial-epc-devon" />
        <script type="application/ld+json">{JSON.stringify(localSchema)}</script>
      </Helmet>
      
      <Navbar />
      <Breadcrumbs items={[{ label: 'Industrial EPC', path: '/industrial-epc' }, { label: 'Devon', path: '/industrial-epc-devon' }]} />
      
      <section className="bg-gradient-to-br from-green-800 via-green-700 to-green-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-200 px-4 py-2 rounded-full text-sm mb-6">
              <MapPin className="w-4 h-4" /> Devon
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Industrial EPC Assessments in Devon</h1>
            <p className="text-xl text-green-200 mb-8">Professional warehouse, factory and workshop EPC surveys across Exeter, Plymouth, Barnstaple and throughout Devon.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-green-700 hover:bg-gray-100">
                <Link to="/booking?service=commercial_warehouse">Get Devon EPC Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:01392980077"><Phone className="mr-2 w-5 h-5" />Call 01392 980077</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Industrial EPC Services Across Devon</h2>
            <p className="text-gray-700">
              Devon hosts a diverse industrial economy ranging from maritime engineering in Plymouth to agricultural processing in rural areas. Energy Performance Direct provides professional <Link to="/industrial-epc" className="text-green-600 hover:underline">industrial EPC assessments</Link> throughout the county, serving landlords, developers and business owners who require compliant energy certificates for warehouses, factories, workshops and distribution centres.
            </p>
            <p className="text-gray-700">
              Our accredited non-domestic energy assessors cover all major Devon towns including Exeter, Plymouth, Barnstaple, Newton Abbot, Tiverton, Exmouth, Honiton, Crediton, Ivybridge and Tavistock. We regularly assess industrial properties on established trading estates such as Marsh Barton and Sowton in Exeter, Langage Business Park near Plymouth, and Roundswell in Barnstaple.
            </p>
            <p className="text-gray-700">
              All non-domestic properties in Devon require an Energy Performance Certificate when being sold, let or constructed. For industrial landlords, the Minimum Energy Efficiency Standards (MEES) regulations require rental properties to achieve at least an E rating. Our assessors provide detailed SBEM-calculated certificates with improvement recommendations to help you meet compliance requirements.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Devon Industrial Areas We Cover</h2>
            <div className="not-prose grid md:grid-cols-2 gap-4">
              {industrialEstates.map((estate, i) => (
                <div key={i} className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900">{estate.name}</h3>
                  <p className="text-sm text-green-600 mb-2">{estate.location}</p>
                  <p className="text-sm text-gray-600">{estate.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Towns Covered in Devon</h2>
            <p className="text-gray-700">We provide industrial EPC assessments throughout Devon including:</p>
            <div className="not-prose flex flex-wrap gap-2 mt-4">
              {towns.map((town, i) => (
                <span key={i} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">{town}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/industrial-epc" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Factory className="w-6 h-6 text-green-600 mb-2" />
                <h3 className="font-semibold">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">All industrial building types</p>
              </Link>
              <Link to="/warehouse-epc" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Building2 className="w-6 h-6 text-green-600 mb-2" />
                <h3 className="font-semibold">Warehouse EPCs</h3>
                <p className="text-sm text-gray-600">Distribution and storage</p>
              </Link>
              <Link to="/commercial-epc-devon" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Building2 className="w-6 h-6 text-green-600 mb-2" />
                <h3 className="font-semibold">Commercial EPCs Devon</h3>
                <p className="text-sm text-gray-600">All commercial buildings</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Get Your Devon Industrial EPC Quote</h2>
          <p className="text-xl text-green-100 mb-8">Professional assessments throughout Devon.</p>
          <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
            <Link to="/booking?service=commercial_warehouse">Request Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
          </Button>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default IndustrialEPCDevon;